import React from 'react';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip
} from '@mui/material';
import {
  DonutSmall
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

/**
 * CorrelationHeatmapCard Component
 * 
 * Displays correlation analysis for portfolio:
 * - Correlation matrix heatmap
 * - High correlation pairs
 * - Diversification metrics
 * - Color-coded correlation levels
 * 
 * NEW - Phase 1 Day 7: Correlation Visualization
 * 
 * @param {Object} correlationMetrics - Correlation data from API
 */
const CorrelationHeatmapCard = ({ correlationMetrics }) => {
  const { t } = useTranslation();

  if (!correlationMetrics || !correlationMetrics.correlation_matrix) return null;

  const {
    correlation_matrix = {},
    avg_correlation = 0,
    high_correlation_pairs = [],
    correlation_penalty = 0,
    diversification_ratio = 1.0,
    num_stocks = 0
  } = correlationMetrics;

  // Get tickers from correlation matrix
  const tickers = Object.keys(correlation_matrix);

  // Helper: Get correlation color
  const getCorrelationColor = (corr) => {
    const absCorr = Math.abs(corr);
    if (absCorr < 0.3) return '#4caf50'; // Green (low correlation)
    if (absCorr < 0.6) return '#ff9800'; // Orange (moderate)
    return '#f44336'; // Red (high correlation)
  };

  // Helper: Get correlation background color
  const getCorrelationBgColor = (corr) => {
    const absCorr = Math.abs(corr);
    if (absCorr < 0.3) return 'rgba(76, 175, 80, 0.1)';
    if (absCorr < 0.6) return 'rgba(255, 152, 0, 0.15)';
    return 'rgba(244, 67, 54, 0.2)';
  };

  // Helper: Get diversification color
  const getDiversificationColor = (dr) => {
    if (dr >= 1.5) return 'success';
    if (dr >= 1.2) return 'warning';
    return 'error';
  };

  // Helper: Get penalty color
  const getPenaltyColor = (penalty) => {
    if (penalty < 0.3) return 'success';
    if (penalty < 0.6) return 'warning';
    return 'error';
  };

  return (
    <Card sx={{ p: 2.5, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <DonutSmall sx={{ color: 'info.main', fontSize: 28 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
            {t('correlation.title')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
            {num_stocks} {t('correlation.stocks')}
          </Typography>
        </Box>
      </Box>
      {/* Summary Metrics - Compact Grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5, mb: 2 }}>
        {/* Average Correlation */}
        <Tooltip title={t('correlation.tooltips.avgCorrelation')} arrow>
          <Box sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
              {t('correlation.metrics.avgCorrelation')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
              {avg_correlation.toFixed(2)}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem' }}>
              {avg_correlation < 0.3 ? t('correlation.levels.low') : 
               avg_correlation < 0.6 ? t('correlation.levels.moderate') : 
               t('correlation.levels.high')}
            </Typography>
          </Box>
        </Tooltip>

        {/* Diversification Ratio */}
        <Tooltip title={t('correlation.tooltips.diversificationRatio')} arrow>
          <Box sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
              {t('correlation.metrics.diversificationRatio')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: getDiversificationColor(diversification_ratio) + '.main', fontSize: '1.1rem' }}>
              {diversification_ratio.toFixed(2)}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem' }}>
              {diversification_ratio >= 1.5 ? t('correlation.drLevels.good') : 
               diversification_ratio >= 1.2 ? t('correlation.drLevels.moderate') : 
               t('correlation.drLevels.poor')}
            </Typography>
          </Box>
        </Tooltip>

        {/* Correlation Penalty */}
        <Tooltip title={t('correlation.tooltips.correlationPenalty')} arrow>
          <Box sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
              {t('correlation.metrics.correlationPenalty')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: getPenaltyColor(correlation_penalty) + '.main', fontSize: '1.1rem' }}>
              {correlation_penalty.toFixed(2)}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem' }}>
              {correlation_penalty < 0.3 ? t('correlation.penaltyLevels.low') : 
               correlation_penalty < 0.6 ? t('correlation.penaltyLevels.moderate') : 
               t('correlation.penaltyLevels.high')}
            </Typography>
          </Box>
        </Tooltip>

        {/* High Correlation Pairs */}
        <Tooltip title={t('correlation.tooltips.highPairs')} arrow>
          <Box sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
              {t('correlation.metrics.highPairs')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
              {high_correlation_pairs.length}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem' }}>
              {high_correlation_pairs.length === 0 ? t('correlation.pairLevels.none') : 
               high_correlation_pairs.length <= 3 ? t('correlation.pairLevels.few') : 
               t('correlation.pairLevels.many')}
            </Typography>
          </Box>
        </Tooltip>
      </Box>

      {/* Correlation Matrix Heatmap */}
      {tickers.length > 0 && tickers.length <= 20 && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontSize: '0.85rem' }}>
            {t('correlation.heatmap.title')}
          </Typography>
          <TableContainer sx={{ maxHeight: 300, mb: 2 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', minWidth: 60, py: 1 }}>
                    {t('correlation.heatmap.ticker')}
                  </TableCell>
                  {tickers.map((ticker) => (
                    <TableCell 
                      key={ticker} 
                      align="center" 
                      sx={{ fontWeight: 700, fontSize: '0.7rem', minWidth: 50, py: 1 }}
                    >
                      {ticker}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tickers.map((ticker1) => (
                  <TableRow key={ticker1} hover>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75 }}>
                      {ticker1}
                    </TableCell>
                    {tickers.map((ticker2) => {
                      const corr = correlation_matrix[ticker1]?.[ticker2] || 0;
                      const isDiagonal = ticker1 === ticker2;
                      
                      return (
                        <TableCell 
                          key={ticker2} 
                          align="center"
                          sx={{
                            bgcolor: isDiagonal ? 'action.hover' : getCorrelationBgColor(corr),
                            fontSize: '0.7rem',
                            fontWeight: isDiagonal ? 700 : 600,
                            color: isDiagonal ? 'text.primary' : getCorrelationColor(corr),
                            py: 0.75
                          }}
                        >
                          <Tooltip title={`${ticker1} vs ${ticker2}: ${corr.toFixed(2)}`} arrow>
                            <span>{corr.toFixed(2)}</span>
                          </Tooltip>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
              </Table>
          </TableContainer>

          {/* Legend */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 14, height: 14, bgcolor: '#4caf50', borderRadius: 0.5 }} />
              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                {t('correlation.legend.low')} (&lt; 0.3)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 14, height: 14, bgcolor: '#ff9800', borderRadius: 0.5 }} />
              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                {t('correlation.legend.moderate')} (0.3-0.6)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 14, height: 14, bgcolor: '#f44336', borderRadius: 0.5 }} />
              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                {t('correlation.legend.high')} (&gt; 0.6)
              </Typography>
            </Box>
          </Box>
        </>
      )}

      {/* High Correlation Pairs */}
      {high_correlation_pairs.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontSize: '0.85rem' }}>
            {t('correlation.highPairs.title')}
          </Typography>
          <TableContainer sx={{ maxHeight: 180 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>
                    {t('correlation.highPairs.pair')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>
                    {t('correlation.highPairs.correlation')}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>
                    {t('correlation.highPairs.level')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {high_correlation_pairs.slice(0, 10).map((pair, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell sx={{ fontSize: '0.75rem', py: 0.75 }}>
                      {pair.ticker1} ↔ {pair.ticker2}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600, py: 0.75 }}>
                      {pair.correlation.toFixed(2)}
                    </TableCell>
                    <TableCell align="center" sx={{ py: 0.75 }}>
                      <Chip
                        label={
                          pair.correlation >= 0.8 ? t('correlation.levels.veryHigh') :
                          pair.correlation >= 0.7 ? t('correlation.levels.high') :
                          t('correlation.levels.moderate')
                        }
                        color={
                          pair.correlation >= 0.8 ? 'error' :
                          pair.correlation >= 0.7 ? 'warning' :
                          'default'
                        }
                        size="small"
                        sx={{ fontWeight: 600, fontSize: '0.65rem', height: 20 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Too Many Stocks Warning */}
      {tickers.length > 20 && (
        <Box sx={{ 
          p: 1.5, 
          bgcolor: 'info.lighter', 
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'info.light'
        }}>
          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
            {t('correlation.tooManyStocks', { count: tickers.length })}
          </Typography>
        </Box>
      )}
    </Card>
  );
};

export default CorrelationHeatmapCard;
